import { Image } from "@/components/ui/image";
import Link from "next/link";
import { formatConditionLabel, formatDimensionsLabel } from "@shared/products/product-display";
import { fillDistanceMiles } from "@shared/listings/fill-distance";
import { statesStockToBuyers } from "@shared/products/listing-stock";
import { formatCents } from "@/lib/formatting/currency";
import { formatListingLocation } from "@/lib/formatting/geography";
import { dockCoordinates, type BuilderListing, type Dock } from "@/lib/listings/builder-state";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";

interface YardListProps {
  listings: BuilderListing[];
  total: number;
  truncated: boolean;
  dock: Dock | null;
  picked: BuilderListing[];
  onTogglePick: (listing: BuilderListing) => void;
  quotePending: boolean;
  pickingDisabled: boolean;
  onQuoteSpec: () => void;
}

export function distanceFor(listing: BuilderListing, dock: Dock | null): number | null {
  const origin = dockCoordinates(dock);
  if (!origin || listing.cityLatitude === null || listing.cityLongitude === null) return null;
  return fillDistanceMiles(
    origin,
    { latitude: listing.cityLatitude, longitude: listing.cityLongitude },
  );
}

export function sortByDistance(listings: BuilderListing[], dock: Dock | null) {
  if (!dock) return listings;
  return listings.slice().sort(
    (left, right) =>
      (distanceFor(left, dock) ?? Number.POSITIVE_INFINITY) -
      (distanceFor(right, dock) ?? Number.POSITIVE_INFINITY),
  );
}

function listingHref(listing: BuilderListing): string {
  return `/listing/${listing.slug ?? listing.publicIdentifier ?? listing._id}`;
}

function YardMeta({ listing }: { listing: BuilderListing }) {
  return (
    <div className="min-w-0">
      <Link href={listingHref(listing)} className="block truncate text-sm font-semibold text-foreground hover:text-action">
        {listing.title}
      </Link>
      <div className="mt-0.5 truncate tabular-nums text-xs text-muted-foreground">
        {[
          formatConditionLabel(listing.condition, listing.productTypeSlug),
          formatDimensionsLabel(listing.dimensions),
          formatListingLocation(listing.cityName, listing.stateAbbr, listing.originZip),
          listing.checkoutEnabled ? "buy now" : "quote",
        ]
          .filter(Boolean)
          .join(" · ")}
      </div>
      <div className="mt-1 tabular-nums text-sm font-semibold text-foreground md:hidden">
        {listing.buyerUnitPriceCents ? `${formatCents(listing.buyerUnitPriceCents)} per unit` : "Quote"}
      </div>
    </div>
  );
}

function YardThumb({ listing }: { listing: BuilderListing }) {
  if (!listing.primaryImageUrl) return <span aria-hidden className="block h-14 w-20 rounded-md bg-surface-2" />;
  return (
    <Link href={listingHref(listing)} tabIndex={-1} aria-hidden className="relative block h-14 w-20 overflow-hidden rounded-md bg-surface-2">
      <Image src={listing.primaryImageUrl} alt="" fill sizes="80px" className="object-cover" />
    </Link>
  );
}

function YardStock({ listing }: { listing: BuilderListing }) {
  const available = listing.availableToSell ?? 0;
  if (listing.checkoutEnabled && !statesStockToBuyers(listing)) return null;
  return (
    <div className="hidden text-xs text-muted-foreground md:block">
      {statesStockToBuyers(listing) ? (
        <>
          <b className="block tabular-nums text-base font-semibold text-foreground">{available.toLocaleString()}</b>
          on hand
        </>
      ) : (
        "Stock on request"
      )}
    </div>
  );
}

function YardRow({
  listing,
  dock,
  isPicked,
  onTogglePick,
  disabled,
}: {
  listing: BuilderListing;
  dock: Dock | null;
  isPicked: boolean;
  onTogglePick: () => void;
  disabled: boolean;
}) {
  const distance = distanceFor(listing, dock);
  return (
    <li
      className={cn(
        "grid grid-cols-[auto_1fr_auto] items-center gap-3 border-t border-border-subtle p-3 first:border-t-0 md:grid-cols-[auto_1fr_auto_auto_auto]",
        isPicked && "bg-action-tint shadow-[inset_3px_0_0_var(--action)]",
      )}
    >
      <YardThumb listing={listing} />
      <YardMeta listing={listing} />
      <YardStock listing={listing} />
      <div className="hidden tabular-nums text-lg font-semibold tracking-tight text-foreground md:block">
        {listing.buyerUnitPriceCents ? formatCents(listing.buyerUnitPriceCents) : "Quote"}
        <small className="block font-sans text-xs font-normal text-muted-foreground">
          {distance !== null ? `${Math.round(distance)} mi from your dock` : "per unit"}
        </small>
      </div>
      <Button type="button" size="sm" variant={isPicked ? "primary" : "outline"} onClick={onTogglePick} aria-pressed={isPicked} aria-label={`${isPicked ? "Unpick" : "Pick"} ${listing.title}`} disabled={disabled}>
        {isPicked ? "Picked" : "Pick"}
      </Button>
    </li>
  );
}

export function YardList({ listings, total, truncated, dock, picked, onTogglePick, onQuoteSpec, quotePending, pickingDisabled }: YardListProps) {
  if (listings.length === 0) {
    return (
      <Card className="items-center border-dashed border-border-strong p-7 text-center text-muted-foreground">
        <p className="m-0">
          No yard lists this exact spec today{truncated ? " among the newest 500 listings" : ""}.
        </p>
        <Button type="button" variant="primary" className="mt-3" onClick={onQuoteSpec} disabled={quotePending}>
          {quotePending ? "Locating your dock…" : "Send this spec as a quote request →"}
        </Button>
      </Card>
    );
  }
  return (
    <div>
      <p className="mb-2 text-xs text-muted-foreground">
        Showing {listings.length} of {total}
        {truncated ? "+" : ""} matching listings{dockCoordinates(dock) ? ", nearest to your dock first" : ""}.
      </p>
      <Card asChild className="overflow-hidden rounded-xl" padding="none">
        <ul>
          {sortByDistance(listings, dock).map((listing) => (
            <YardRow
              key={listing._id}
              listing={listing}
              dock={dock}
              isPicked={picked.some((pick) => pick._id === listing._id)}
              onTogglePick={() => onTogglePick(listing)}
              disabled={pickingDisabled}
            />
          ))}
        </ul>
      </Card>
    </div>
  );
}
